import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { getBillingPaymentConfig } from 'api/insuranceBilling';
import type { TokenizedCard } from '../../paymentBridge';
import { paymentBridge, squareBrandLabel } from '../../paymentBridge';
import s from './s.module.less';

// Square Web Payments SDK 卡表单（hosted iframe，卡号不落前端）。挂载时把 tokenize 注册到
// paymentBridge，由保存逻辑（savePaymentMethodStep）在点 Continue / Save changes 时调用。
// window.Square 的全局类型声明由 ChargeCardModal 提供（declare global 全项目可见）

interface SquareCard {
    attach: (selector: string) => Promise<void>;
    tokenize: (opts?: { intent?: string }) => Promise<{ status: string; token?: string; errors?: unknown }>;
    destroy: () => Promise<void>;
}

// sandbox / production 由 payment-config 返回的 applicationId 前缀判定（后端是两个 id 的唯一
// 来源，不看前端 env——isLive 口径可能与后端配置不同步）
const squareScriptUrl = (applicationId: string) =>
    applicationId.startsWith('sandbox-')
        ? 'https://sandbox.web.squarecdn.com/v1/square.js'
        : 'https://web.squarecdn.com/v1/square.js';

// 动态加载 Square 脚本（与 ChargeCardModal 同款模式）
const loadSquareScript = (url: string): Promise<boolean> => {
    if (typeof window === 'undefined') return Promise.resolve(false);
    if (window.Square) return Promise.resolve(true);
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) return Promise.resolve(!!window.Square);
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(!!window.Square);
        script.onerror = () => resolve(false);
        document.head.appendChild(script);
    });
};

// attach 走 id 选择器（全局类型只接受 selector）；向导 / Review / Settings 弹窗都可能各挂一次，
// 自增后缀保证 id 唯一
let containerSeq = 0;

export default function SquareCardForm() {
    const [containerId] = useState(() => {
        containerSeq += 1;
        return `ibb-square-card-${containerSeq}`;
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const cardRef = useRef<SquareCard | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getBillingPaymentConfig();
                if (cancelled) return;
                // 91023 = 该环境没配 Square（接口 disableErrorNote，错误只在这里内联展示）
                if (res?.error) {
                    throw new Error(res?.data?.message || 'Payment is not available in this environment.');
                }
                const data = res?.data?.data;
                const applicationId: string | undefined = data?.applicationId;
                const locationId: string | undefined = data?.locationId;
                if (!applicationId || !locationId) {
                    throw new Error('Payment is not configured. Missing Square credentials.');
                }
                const loaded = await loadSquareScript(squareScriptUrl(applicationId));
                if (cancelled) return;
                if (!loaded || !window.Square) throw new Error('Failed to load payment form.');
                const card = await window.Square.payments(applicationId, locationId).card();
                if (cancelled) {
                    card.destroy().catch(() => {});
                    return;
                }
                cardRef.current = card;
                await card.attach(`#${containerId}`);
                if (!cancelled) {
                    setLoading(false);
                    // 加载期间点 Continue 会留下 "not ready" 错误，表单就绪后它已过时
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError((e as Error)?.message || 'Failed to initialize payment form.');
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
            if (cardRef.current) {
                cardRef.current.destroy().catch(() => {});
                cardRef.current = null;
            }
        };
    }, [containerId]);

    useEffect(() => {
        paymentBridge.tokenize = async (): Promise<TokenizedCard | null> => {
            const card = cardRef.current;
            if (!card) {
                setError('Payment form is not ready yet.');
                return null;
            }
            setError(null);
            try {
                const result: any = await card.tokenize();
                if (result?.status === 'OK' && result?.token) {
                    const detail = result?.details?.card || {};
                    return {
                        token: result.token,
                        card: { brand: squareBrandLabel(detail.brand), last4: detail.last4 || '' },
                    };
                }
                const first = Array.isArray(result?.errors) ? result.errors[0]?.message : undefined;
                setError(first || 'Invalid card. Please check and try again.');
                return null;
            } catch (e) {
                setError((e as Error)?.message || 'Card tokenization failed.');
                return null;
            }
        };
        return () => {
            paymentBridge.tokenize = null;
        };
    }, []);

    return (
        <div>
            {loading && (
                <div className={s.loading}>
                    <Spin size="small" /> Loading payment form…
                </div>
            )}
            {/* attach 需要容器已在 DOM 里，加载期用 visibility 隐藏而非卸载 */}
            <div id={containerId} style={{ visibility: loading ? 'hidden' : 'visible' }} />
            {/* 校验/初始化错误挂在表单下方（tokenize 失败时紧贴出错的卡片输入区） */}
            {error && <div className={s.error}>{error}</div>}
            <p className={s.foot}>Processed securely by Square. Klarity never stores your full card number.</p>
        </div>
    );
}
